import type { DashboardOrder, OrderDetails } from '../model'
import { dayjs } from '@/shared/lib/dayjs'
import { getDriverName, getOrderType } from './hsFields.getters'

/**
 * Order types that are considered partner orders
 */
export const PARTNER_ORDER_TYPES = [
  'Partner Delivery',
  'Rapid Delivery',
  'Early Rapid Delivery',
  'Early Partner Delivery',
] as const

export type PartnerOrderType = typeof PARTNER_ORDER_TYPES[number]

/**
 * Maximum time gap in minutes for grouping partner orders with the same driver
 */
export const PARTNER_PICKUP_TIME_THRESHOLD_MINS = 15

/**
 * Interface for partner order grouping configuration
 */
export interface PartnerOrderGroupingConfig {
  maxGroupSize: number
}

/**
 * Default configuration for partner order grouping
 */
export const DEFAULT_PARTNER_GROUPING_CONFIG: PartnerOrderGroupingConfig = {
  maxGroupSize: 2,
}

/**
 * Validates if an order has all required fields for partner order grouping
 */
export function hasRequiredPartnerFields(order: OrderDetails): boolean {
  const driverName = getDriverName(order.hsFields)
  const partnerPickupTime = order.partnerPickupTime
  const orderType = getOrderType(order.hsFields)

  return !!(driverName && partnerPickupTime && orderType)
}

/**
 * Checks if an order type is a partner order type
 */
export function isPartnerOrderType(orderType: string | undefined): orderType is PartnerOrderType {
  return orderType ? PARTNER_ORDER_TYPES.includes(orderType as PartnerOrderType) : false
}

/**
 * Checks if two pickup times are within the specified threshold
 * @param time1 - First pickup time (ISO string)
 * @param time2 - Second pickup time (ISO string)
 * @param thresholdMins - Maximum allowed time difference in minutes (default: 15)
 * @returns true if times are within threshold, false otherwise
 */
export function arePickupTimesWithinThreshold(
  time1: string,
  time2: string,
  thresholdMins: number = PARTNER_PICKUP_TIME_THRESHOLD_MINS,
): boolean {
  const date1 = dayjs(time1)
  const date2 = dayjs(time2)

  const diffMinutes = Math.abs(date1.diff(date2, 'minute'))

  return diffMinutes <= thresholdMins
}

/**
 * Creates a single dashboard order from an OrderDetails
 */
function createSingleDashboardOrder(order: OrderDetails): DashboardOrder {
  return {
    type: 'single',
    id: order.orderId,
    data: order,
  }
}

/**
 * Creates a group dashboard order from a collection of orders
 */
function createGroupDashboardOrder(groupKey: string, orders: OrderDetails[]): DashboardOrder {
  return {
    type: 'group',
    id: groupKey,
    data: {
      id: groupKey,
      status: orders[0].status,
      assignee: getDriverName(orders[0].hsFields) || 'Unknown',
      orders,
    },
  }
}

/**
 * Formats partner orders into dashboard cards based on max group size constraints
 * Partner orders always use group cards, even if single
 */
function formatOrdersByMaxGroupSize(
  groupKey: string,
  groupOrders: OrderDetails[],
  config: PartnerOrderGroupingConfig,
): DashboardOrder[] {
  const result: DashboardOrder[] = []

  if (groupOrders.length === 1) {
    // Single partner order - always use group card
    result.push(createGroupDashboardOrder(groupKey, groupOrders))
  } else {
    // Multiple partner orders - group them according to maxGroupSize
    for (let i = 0; i < groupOrders.length; i += config.maxGroupSize) {
      const ordersToProcess = groupOrders.slice(i, i + config.maxGroupSize)

      if (ordersToProcess.length === 1) {
        // Single remaining partner order - use group card
        const singleGroupKey = `${groupKey}-${Math.floor(i / config.maxGroupSize)}`
        result.push(createGroupDashboardOrder(singleGroupKey, ordersToProcess))
      } else {
        // Create grouped order
        const multiGroupKey = `${groupKey}-${Math.floor(i / config.maxGroupSize)}`
        result.push(createGroupDashboardOrder(multiGroupKey, ordersToProcess))
      }
    }
  }

  return result
}

/**
 * Groups partner orders based on Driver Name and partnerPickupTime within a 15-minute window
 * with a maximum of 2 orders per group
 */
export function groupPartnerOrders(
  orders: OrderDetails[],
  config: PartnerOrderGroupingConfig = DEFAULT_PARTNER_GROUPING_CONFIG,
): DashboardOrder[] {
  const result: DashboardOrder[] = []

  // Maps for grouping
  const partnerGroupingMap = new Map<string, OrderDetails[]>()
  const individualOrders: OrderDetails[] = []

  for (const order of orders) {
    // Skip completed and cancelled orders
    if (order.status === 'Completed' || order.status === 'Fulfilled' || order.status === 'Cancelled') {
      continue
    }

    const orderType = getOrderType(order.hsFields)
    const isPartnerType = isPartnerOrderType(orderType)
    const hasRequiredFields = hasRequiredPartnerFields(order)

    // Check if order qualifies for partner grouping
    if (isPartnerType && hasRequiredFields) {
      const driverName = getDriverName(order.hsFields)!
      const partnerPickupTime = order.partnerPickupTime!

      // Find group with same driver and pickup time within threshold
      let foundGroup = false

      for (const [groupKey, groupOrders] of partnerGroupingMap.entries()) {
        // Only check groups that have the same driver name
        if (groupKey.startsWith(`${driverName}:`)) {
          // Check if pickup times are within threshold
          const referencePickupTime = groupOrders[0].partnerPickupTime!
          if (arePickupTimesWithinThreshold(partnerPickupTime, referencePickupTime)) {
            partnerGroupingMap.get(groupKey)!.push(order)
            foundGroup = true
            break
          }
        }
      }

      // If no matching group found, create a new one
      if (!foundGroup) {
        const groupKey = `${driverName}:${partnerPickupTime}`
        partnerGroupingMap.set(groupKey, [order])
      }
    } else {
      // Order becomes individual
      individualOrders.push(order)
    }
  }

  // Process partner groups
  for (const [groupKey, groupOrders] of partnerGroupingMap.entries()) {
    const groupResults = formatOrdersByMaxGroupSize(groupKey, groupOrders, config)
    result.push(...groupResults)
  }

  // Process individual orders
  for (const order of individualOrders) {
    result.push(createSingleDashboardOrder(order))
  }

  return result
}
